import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";

interface VisibilityContextType {
  visible: boolean;
  toggleVisibility: () => void;
  mask: (value: string | number | null | undefined, placeholder?: string) => string;
}

const VisibilityContext = createContext<VisibilityContextType>({
  visible: true,
  toggleVisibility: () => {},
  mask: (v) => String(v ?? ""),
});

const STORAGE_KEY = "vazoul-visibility-mode";

// Regex that matches numbers in text: integers, decimals, percentages, currency values, negative numbers
const NUMBER_REGEX = /[+\-]?\$?\s*\d[\d,]*\.?\d*\s*%?/g;

// CSS class applied to elements whose text nodes contain numbers
const BLUR_CLASS = "vzl-num-blur";

function isInsideIgnored(node: Node): boolean {
  let el: Element | null = node.nodeType === Node.ELEMENT_NODE
    ? (node as Element)
    : node.parentElement;
  while (el) {
    const tag = el.tagName;
    if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT" || tag === "CODE" || tag === "PRE" || tag === "TEXTAREA") return true;
    if (tag === "INPUT" || tag === "SELECT") return true;
    if (el.hasAttribute("data-no-mask")) return true;
    el = el.parentElement;
  }
  return false;
}

function processTextNode(node: Text, hide: boolean) {
  if (isInsideIgnored(node)) return;
  const parent = node.parentElement;
  if (!parent) return;

  const text = node.textContent || "";
  if (!text.trim()) return;

  if (hide) {
    // Check if this text has numbers worth blurring
    NUMBER_REGEX.lastIndex = 0;
    if (!NUMBER_REGEX.test(text)) return;
    // Add blur class to the parent element — no text replacement
    parent.classList.add(BLUR_CLASS);
  } else {
    // Remove blur class
    parent.classList.remove(BLUR_CLASS);
  }
}

function walkAndProcess(root: Node, hide: boolean) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes: Text[] = [];
  let current: Text | null;
  while ((current = walker.nextNode() as Text | null)) {
    nodes.push(current);
  }
  for (const n of nodes) {
    processTextNode(n, hide);
  }
}

export function VisibilityProvider({ children }: { children: ReactNode }) {
  const [visible, setVisible] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });

  const observerRef = useRef<MutationObserver | null>(null);

  // Sync CSS class on <html> for global CSS-based masking (charts, SVG, etc.)
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.classList.add("visibility-off");
    } else {
      root.classList.remove("visibility-off");
    }
  }, [visible]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(visible));
    } catch {}
  }, [visible]);

  // Global DOM number masking via blur class
  useEffect(() => {
    const timer = setTimeout(() => {
      walkAndProcess(document.body, !visible);
    }, 50);

    if (!visible) {
      const obs = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === "childList") {
            for (const node of m.addedNodes) {
              if (node.nodeType === Node.TEXT_NODE) {
                processTextNode(node as Text, true);
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                walkAndProcess(node, true);
              }
            }
          }
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
      observerRef.current = obs;
    } else {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    }

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [visible]);

  const toggleVisibility = useCallback(() => setVisible((v) => !v), []);

  const mask = useCallback(
    (value: string | number | null | undefined) => {
      // Always return the real value — blur is handled via CSS, not text replacement
      return String(value ?? "");
    },
    []
  );

  return (
    <VisibilityContext.Provider value={{ visible, toggleVisibility, mask }}>
      {children}
    </VisibilityContext.Provider>
  );
}

export function useVisibility() {
  return useContext(VisibilityContext);
}
